import {
  Button,
  DatePicker,
  FormField,
  Input,
  Multiselect,
  Select,
  SelectProps,
  SpaceBetween,
} from '@cloudscape-design/components';
import useUserProfile from 'src/hooks/useUserProfile';
import { useEffect } from 'react';
import { useDivision } from 'src/store/division/useDivision';
import { Division } from 'src/types/orbit/Division';
import { SearchTasksRequest } from 'src/types/helios/Task';
import SingleSelectDivision from '../division/SingleSelectDivision';
import EmployeePicker from '../common/EmployeePicker';
import { convertList } from 'src/utils/generalUtils';
import { useLocations } from 'src/store/locations/useLocations';
import { Location } from 'src/types/helios/Location';
import { useTemplates } from 'src/store/templates/useTemplates';
import { AuditTemplate } from '@amzn/gbsa-helios-typescript-client';
interface TaskFilterViewProps {
  onSearchChange: (searchParams: SearchTasksRequest) => void;
  currentSearchParams?: SearchTasksRequest;
  resetSearchParams?: SearchTasksRequest; // What to reset to (from parent)
  onClearAll?: () => void;
}

const statusOptions = [
  { label: 'Completed', value: 'COMPLETED' },
  { label: 'In Progress', value: 'IN_PROGRESS' },
  { label: 'Assigned', value: 'ASSIGNED' },
  { label: 'Overdue', value: 'OVERDUE' },
  { label: 'Cancelled', value: 'CANCELLED' },
];

const taskTypeOptions = [
  { label: 'Failed Question', value: 'FAILED_QUESTION' },
  { label: 'Observation', value: 'OBSERVATION' },
];

const dateRangeList = [
  { label: 'Past Week', value: 'PAST_WEEK' },
  { label: 'Past Month', value: 'PAST_MONTH' },
  { label: 'Past Year', value: 'PAST_YEAR' },
  { label: 'Custom', value: 'CUSTOM' },
];

const TaskFilterView = ({
  onSearchChange,
  currentSearchParams,
  resetSearchParams,
  onClearAll,
}: TaskFilterViewProps) => {
  const { getUserBusinessUnitId, getUserProgramId, currentUser, getLocations } =
    useUserProfile();
  const { divisionList, fetcDivisionsApiStatus, loadDivisionsByBusinessUnit } =
    useDivision();
  const { locationsList, loadLocations } = useLocations();
  const { templatesList, loadTemplatesList } = useTemplates();

  const initSearchParams: SearchTasksRequest = {
    businessUnitId: getUserBusinessUnitId(),
    locations: getLocations(), // User preferences
    // Explicitly clear all other filters
    taskName: undefined,
    taskStatuses: undefined,
    assigneeLogin: undefined,
    auditId: undefined,
    taskType: undefined,
    questionId: undefined,
    dateRange: undefined,
    language: undefined,
    divisionName: undefined,
    keyword: undefined,
    templateId: undefined,
  };

  // Use currentSearchParams directly as the source of truth (controlled component)
  const searchParams = currentSearchParams || initSearchParams;

  // Load data when component mounts or business unit changes
  useEffect(() => {
    if (searchParams.businessUnitId) {
      loadDivisionsByBusinessUnit({
        businessUnitId: getUserBusinessUnitId(),
        programId: getUserProgramId(),
        isDeleted: false,
        isActive: true,
      });
      loadLocations({
        businessUnitId: getUserBusinessUnitId(),
      });
      loadTemplatesList({
        program: getUserProgramId(),
        businessUnitId: getUserBusinessUnitId(),
      });
    }
  }, [currentUser]);

  return (
    <SpaceBetween size="l">
      <FormField label="Location" data-testid="location-field">
        <Multiselect
          filteringType="auto"
          data-testid="location-dropdown"
          options={convertList<Location, SelectProps.Option>(locationsList, (item) => ({
            value: item.locationCode,
            label: item.locationCode,
          }))}
          enableSelectAll
          selectedOptions={
            searchParams?.locations?.map((loc) => ({
              label: loc,
              value: loc,
            })) || []
          }
          inlineTokens
          onChange={({ detail }) => {
            const locations = detail.selectedOptions.map((opt) => opt.value as string);
            const newParams = {
              ...searchParams,
              locations: locations.length > 0 ? locations : undefined,
            };
            onSearchChange(newParams);
          }}
          loadingText="Loading Location"
          placeholder="Choose Location"
        />
      </FormField>

      <FormField label="Division" data-testid="division-field">
        <SingleSelectDivision
          divisions={divisionList}
          isLoading={fetcDivisionsApiStatus.isLoading}
          error={fetcDivisionsApiStatus.errorMessage}
          selectedDivision={searchParams.divisionName || null}
          onItemSelection={(item: Division | null) => {
            const divisionName = item?.divisionName || '';
            const newParams = {
              ...searchParams,
              divisionName: divisionName || undefined,
            };
            onSearchChange(newParams);
          }}
        />
      </FormField>

      <FormField label="Template" data-testid="template-field">
        <Select
          selectedOption={(() => {
            const selectedTemplate = searchParams.templateId
              ? templatesList.find((t) => t.id === searchParams.templateId)
              : undefined;
            return selectedTemplate
              ? {
                  label: selectedTemplate.templateName || '',
                  value: selectedTemplate.id || '',
                }
              : null;
          })()}
          onChange={({ detail }) => {
            const newParams = {
              ...searchParams,
              templateId: detail.selectedOption?.value || undefined,
            };
            onSearchChange(newParams);
          }}
          options={convertList<AuditTemplate, SelectProps.Option>(
            templatesList,
            (item) => ({
              value: item.id,
              label: item.templateName,
            }),
          )}
          placeholder="Select template"
        />
      </FormField>

      <FormField label="Task Type" data-testid="task-type-field">
        <Select
          selectedOption={
            taskTypeOptions.find((option) => option.value === searchParams.taskType) ||
            null
          }
          onChange={({ detail }) => {
            const newParams = {
              ...searchParams,
              taskType: detail.selectedOption?.value || undefined,
            };
            onSearchChange(newParams);
          }}
          options={taskTypeOptions}
          placeholder="Select task type"
        />
      </FormField>

      <FormField label="Task Status" data-testid="status-field">
        <Select
          selectedOption={
            statusOptions.find(
              (option) => option.value === searchParams?.taskStatuses?.[0],
            ) || null
          }
          onChange={({ detail }) => {
            const newParams = {
              ...searchParams,
              taskStatuses: detail.selectedOption?.value
                ? [detail.selectedOption.value]
                : undefined,
            };
            onSearchChange(newParams);
          }}
          options={statusOptions}
          placeholder="Select status"
        />
      </FormField>

      <FormField label="Assignee Login" data-testid="assignee-field">
        <EmployeePicker
          programId={getUserProgramId()}
          value={searchParams.assigneeLogin || ''}
          data-testid="assignee-input"
          onSelect={(login) => onSearchChange({ ...searchParams, assigneeLogin: login })}
          onClear={() => onSearchChange({ ...searchParams, assigneeLogin: undefined })}
          placeholder="Search by alias or name"
        />
      </FormField>

      <FormField label="Audit ID" data-testid="audit-id-field">
        <Input
          value={searchParams.auditId || ''}
          onChange={({ detail }) => {
            const newParams = {
              ...searchParams,
              auditId: detail.value || undefined,
            };
            onSearchChange(newParams);
          }}
          placeholder="Enter audit ID"
        />
      </FormField>

      <FormField label="Question ID" data-testid="question-id-field">
        <Input
          value={searchParams.questionId || ''}
          onChange={({ detail }) => {
            const newParams = {
              ...searchParams,
              questionId: detail.value || undefined,
            };
            onSearchChange(newParams);
          }}
          placeholder="Enter question ID"
        />
      </FormField>

      <FormField label="Due Date Range" data-testid="due-date-range-field">
        <Select
          selectedOption={
            searchParams.dateRange?.type
              ? dateRangeList.find((item) => item.value === searchParams.dateRange?.type)!
              : null
          }
          onChange={({ detail }) => {
            const newParams = {
              ...searchParams,
              dateRange: {
                type: detail.selectedOption.value as string,
              },
            };
            onSearchChange(newParams);
          }}
          options={dateRangeList}
          placeholder="Select due date range"
        />
      </FormField>

      {searchParams?.dateRange?.type === 'CUSTOM' && (
        <>
          <FormField
            label="Due Date Start"
            stretch
            errorText={
              searchParams.dateRange?.startDate &&
              searchParams.dateRange?.endDate &&
              new Date(searchParams.dateRange.startDate) >
                new Date(searchParams.dateRange.endDate)
                ? 'Start date cannot be after end date'
                : undefined
            }
          >
            <DatePicker
              onChange={({ detail }) => {
                const newParams = {
                  ...searchParams,
                  dateRange: {
                    ...searchParams.dateRange,
                    type: 'CUSTOM',
                    startDate: detail.value,
                  },
                };
                onSearchChange(newParams);
              }}
              placeholder="YYYY/MM/DD"
              value={searchParams.dateRange?.startDate || ''}
            />
          </FormField>

          <FormField
            label="Due Date End"
            stretch
            errorText={
              searchParams.dateRange?.startDate &&
              searchParams.dateRange?.endDate &&
              new Date(searchParams.dateRange.startDate) >
                new Date(searchParams.dateRange.endDate)
                ? 'End date cannot be before start date'
                : undefined
            }
          >
            <DatePicker
              onChange={({ detail }) => {
                const newParams = {
                  ...searchParams,
                  dateRange: {
                    ...searchParams.dateRange,
                    type: 'CUSTOM',
                    endDate: detail.value,
                  },
                };
                onSearchChange(newParams);
              }}
              placeholder="YYYY/MM/DD"
              value={searchParams.dateRange?.endDate || ''}
            />
          </FormField>
        </>
      )}

      <SpaceBetween direction="horizontal" size="xs">
        <Button
          data-testid="reset-button"
          onClick={() => {
            // Reset to resetSearchParams (provided by parent - either shared or default filters)
            onSearchChange(resetSearchParams || initSearchParams);
          }}
        >
          Reset
        </Button>
        <Button
          data-testid="clear-all-button"
          onClick={() => {
            if (onClearAll) {
              onClearAll();
            }
          }}
        >
          Clear All
        </Button>
      </SpaceBetween>
    </SpaceBetween>
  );
};

export default TaskFilterView;
